import {
  Box,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ClockIcon } from "components/icons";
import type { Dayjs } from "dayjs";
import { dayjs } from "utilz";

/**
 * TimeField — time picker designed to sit alongside other form fields like
 * `SelectField`, `DateInput`, and `Input` (fixed 32px height, 4px fieldset
 * radius, label vertically centered within the 32px input).
 *
 * Supports standard error state UX:
 *  - red border + error-tint background when `error` is true
 *  - red clock icon when `error` is true
 *  - optional `errorMessage` rendered below the input in the same style
 *    as other form fields (body-small, 14px left margin).
 *
 * For free-form contexts (time range selection, etc.) with the responsive
 * 32/40/48 height and 8px radius, use the sibling `TimeInput` component.
 */
interface TimeFieldProps {
  sx?: SxProps<Theme>;
  value: Dayjs | string | null;
  label?: string;
  disabled?: boolean;
  onChangeHandler: (value: Dayjs | null | string) => void;
  error?: boolean;
  errorMessage?: string;
  timezone?: string;
  /** Asterisk on the label, like `Input` / `SelectField`. */
  required?: boolean;
}

export const TimeField = ({
  sx,
  value,
  label,
  disabled = false,
  onChangeHandler,
  error = false,
  errorMessage,
  timezone,
  required = false,
}: TimeFieldProps) => {
  const theme = useTheme();
  return (
    <Stack sx={{ width: "100%" }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          label={label}
          value={value ? dayjs(value) : null}
          disabled={disabled}
          timezone={timezone}
          // @ts-ignore
          onChange={(newValue) => onChangeHandler(newValue)}
          slots={{
            openPickerIcon: disabled
              ? Box
              : () => (
                  <ClockIcon
                    htmlColor={error ? theme.palette.error.main : undefined}
                  />
                ),
          }}
          slotProps={{
            textField: {
              size: "small",
              error: error,
              required,
              inputProps: { placeholder: "HH:MM" },
              sx: {
                ...theme.typography["body-medium"],
                "& .MuiOutlinedInput-root": {
                  height: "32px",
                  backgroundColor: error ? theme.palette.error.tint : "white",
                  "& > fieldset": {
                    borderColor: theme.palette.neutral["020"],
                    borderRadius: "4px",
                  },
                  "& input::placeholder": { fontSize: "14px" },
                },
                "& .MuiOutlinedInput-root.Mui-focused": {
                  color: theme.palette.lightBg.high,
                  "& > fieldset": {
                    borderColor: theme.palette.purple.main,
                    borderWidth: "1px",
                  },
                },
                "& label": {
                  // Center the label vertically within the 32px height input
                  transform: "translate(14px, 6px) scale(1)",
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                  "&.Mui-focused": { color: theme.palette.purple.main },
                },
                ...sx,
              },
            },
            actionBar: {
              sx: {
                "&.MuiDialogActions-root": {
                  color: `${theme.palette.purple.main} !important`,
                  "& .MuiButtonBase-root": {
                    ...theme.typography.caption,
                    color: `${theme.palette.purple.main} !important`,
                  },
                },
              },
            },
            desktopPaper: {
              sx: {
                "& .MuiMultiSectionDigitalClock-root": {
                  "& .Mui-selected": {
                    backgroundColor: "#EBFCFE",
                    border: `1px solid ${theme.palette.purple.main}`,
                    color: theme.palette.purple.main,
                    borderRadius: "6px",
                  },
                },
                ...theme.typography["body-medium"],
                borderRadius: "16px",
                boxShadow:
                  "0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
              },
            },
          }}
        />
      </LocalizationProvider>
      {errorMessage ? (
        <Typography
          variant="body-small"
          color="error"
          sx={{ mt: "4px", ml: "14px" }}
        >
          {errorMessage}
        </Typography>
      ) : null}
    </Stack>
  );
};
